import React, { useState, useEffect, useCallback } from "react";
import useItems from "../../api/items";
import { smartCompressImage, dataUrlToFile } from "../../lib/imageCompression";
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const ImageDisplayer = ({ imageId, onChange, className }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getDisplayUrl = (id) => {
    if (!id) return null;

    // Case 1: Base64 string (newly selected)
    if (typeof id === "string" && id.startsWith("data:image/")) {
      return id;
    }

    // Case 2: S3 key (e.g. "images/foto.jpg")
    if (typeof id === "string" && id.startsWith("images/")) {
      return `${S3_BASE_URL}/${id}`;
    }

    // Case 3: Full URL (already absolute)
    if (typeof id === "string" && (id.startsWith("http") || id.startsWith("blob:"))) {
      return id;
    }

    return null;
  };

  const imageUrl = getDisplayUrl(imageId);

  const { uploadMedia } = useItems();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setLoading(true);
        setError(null);

        // Comprimeer (incl. HEIC->JPEG) en upload via multipart, dat heeft
        // niet de 2MB-limiet van de oude base64-route.
        const compressedDataUrl = await smartCompressImage(file);
        const compressedFile = dataUrlToFile(compressedDataUrl, file.name);
        const response = await uploadMedia(compressedFile);

        const key =
          response?.data?.mediaKey ??
          response?.data?.imageKey ??
          response?.data?.id;
        if (key) {
          // Pass the S3 KEY to parent (for storage)
          onChange(key);
        } else if (response?.data?.imageUrl || response?.data?.mediaUrl) {
          onChange(response.data.imageUrl || response.data.mediaUrl);
        } else {
          throw new Error("Upload succeeded but no key returned from server.");
        }
      } catch (err) {
        console.error("Upload mislukt:", err);
        setError(
          err.response?.data?.message ||
          err.message ||
          "Er is een fout opgetreden bij het uploaden van de afbeelding"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setImageData(null);
    onChange("");
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-medium
                        file:bg-[#BFFF00] file:text-[#05081A]
                        hover:file:bg-[#BFFF00]/90"
          />
          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        </div>

        {loading && (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#BFFF00]"></div>
        )}

        {imageUrl && !loading && (
          <div className="relative group shrink-0">
            <img
              src={imageUrl}
              alt="Preview"
              className="w-12 h-12 object-cover border rounded-md shadow-sm bg-white"
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-100 shadow-md hover:bg-red-600 transition-colors"
              title="Afbeelding verwijderen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageDisplayer;
